import React from "react";
import { fmtMoney } from "../SSAContext";
import { List } from "../ui";

const H = ({ children }) => <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mt-4 mb-1">{children}</div>;

export function proposalTotals(p) {
  const service = (p.line_items || []).reduce((s, l) => s + Number(l.amount ?? l.quantity * l.rate), 0);
  const pass = (p.pass_throughs || []).reduce((s, x) => s + Number(x.amount), 0);
  return { service, pass, days: (p.line_items || []).reduce((s, l) => s + Number(l.quantity), 0) };
}

export default function ProposalDetail({ p, currency, label, status, testid }) {
  const t = proposalTotals(p);
  return (
    <div className="text-sm min-w-0" data-testid={testid}>
      {label && (
        <div className="flex items-center justify-between gap-2 pb-3 border-b border-slate-100">
          <div className="font-display text-lg font-semibold">{label}</div>
          {status && <span className="text-[11px] font-bold uppercase text-slate-500">{status}</span>}
        </div>
      )}
      <div className="grid gap-2 mt-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
        <div className="rounded-lg bg-[#0B2A4A] text-white p-3"><div className="text-[10px] uppercase tracking-wider text-white/60 font-bold">Service cost</div><div className="font-mono font-semibold whitespace-nowrap" data-testid={`${testid}-service-cost`}>{fmtMoney(t.service, currency)}</div></div>
        <div className="rounded-lg bg-slate-100 p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Pass-throughs</div><div className="font-mono font-semibold whitespace-nowrap">{fmtMoney(t.pass, currency)}</div></div>
        <div className="rounded-lg bg-slate-100 p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{p.monthly_fee ? "Monthly fee" : "Timeline"}</div><div className="font-semibold">{p.monthly_fee ? <span className="font-mono whitespace-nowrap">{fmtMoney(p.monthly_fee, currency)}</span> : `Wk ${p.start_offset_weeks} start · ${p.duration_weeks} wks`}</div></div>
      </div>
      {p.contract_months && (
        <div className="mt-2 text-xs text-slate-600" data-testid={`${testid}-term`}>Contract {p.contract_months} months · service start week {p.start_offset_weeks} · {p.indexation}</div>
      )}
      {p.slas?.length > 0 && (
        <>
          <H>SLA & service-credit regime</H>
          <table className="w-full text-xs" data-testid={`${testid}-slas`}>
            <tbody className="divide-y divide-slate-100">
              {p.slas.map((s) => <tr key={s.metric}><td className="py-1">{s.metric}</td><td className="font-semibold text-right">{s.target}</td><td className="text-right text-slate-500 pl-2">{s.credit}</td></tr>)}
            </tbody>
          </table>
        </>
      )}
      <H>Approach & methodology</H>
      <p className="text-slate-700 leading-relaxed">{p.approach}</p>
      <div className="grid md:grid-cols-2 gap-x-6">
        <div><H>Deliverables</H><List items={p.deliverables} /></div>
        <div><H>Milestones</H><List items={p.milestones} /></div>
      </div>
      <H>Commercial build-up ({p.fee_model})</H>
      <div className="text-xs divide-y divide-slate-100 border-y border-slate-100" data-testid={`${testid}-lines`}>
        {p.line_items.map((l, i) => (
          <div key={i} className="py-2">
            <div className="text-slate-800 break-words">{l.description}</div>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-slate-500">
              <span className="whitespace-nowrap">{l.quantity} {l.unit} × <span className="font-mono">{fmtMoney(l.rate, currency)}</span></span>
              <span className="font-mono text-slate-900 font-semibold whitespace-nowrap ml-auto">{fmtMoney(l.amount ?? l.quantity * l.rate, currency)}</span>
            </div>
          </div>
        ))}
        <div className="py-2 flex flex-wrap items-baseline justify-between gap-x-3 font-bold">
          <span>{p.monthly_fee ? `Contract service value (${p.contract_months} months)` : p.line_items.every((l) => l.unit === "day") ? `Professional subtotal (${t.days} days)` : "Service subtotal"}</span>
          <span className="font-mono whitespace-nowrap ml-auto">{fmtMoney(t.service, currency)}</span>
        </div>
        {(p.pass_throughs || []).map((x) => (
          <div key={x.type} className="py-2 flex flex-wrap items-baseline justify-between gap-x-3 text-slate-600">
            <span className="break-words min-w-0">Pass-through: {x.type} <span className="text-[10px] text-slate-400">({x.approval})</span></span>
            <span className="font-mono whitespace-nowrap ml-auto">{fmtMoney(x.amount, currency)}</span>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-x-6">
        <div><H>Assumptions</H><List items={p.assumptions} /></div>
        <div><H>Exclusions</H><List items={p.exclusions} tone="rose" /></div>
      </div>
      <div className="grid md:grid-cols-2 gap-x-6">
        <div><H>Team / roles</H><List items={p.roles} /></div>
        <div><H>Capability & terms</H><List items={[`Markets covered: ${(p.capability?.markets || []).join(", ") || "—"}`, `Capacity: ${p.capability?.capacity}`, p.capability?.conflict_declaration, `Payment: ${p.payment_terms}`, `SLA: ${p.sla}`, `Validity: ${p.validity_days} days`, `Drafted by: ${p.drafted_by || "partner"}`].filter(Boolean)} /></div>
      </div>
    </div>
  );
}
